<script setup lang="ts" generic="T extends Record<string, unknown>">
import { computed, ref } from "vue";
import { useDraggable } from "@vue-dnd-kit/core";
import TreeDragHandle from "./TreeDragHandle.vue";
import Tree from "./Tree.vue";
import Between from "./Between.vue";
import type { MoveEventPayload } from "./types";

const props = defineProps<{
  data: T;
  source: T[];
  index: number;
  groups: string[];
  expandable: boolean;
  parentId: number | null;
  disable?: boolean;
}>();

const expanded = ref(true);

defineSlots<{
  default: (props: { item: T; expanded: boolean }) => any;
}>();

const emit = defineEmits<{
  (e: "move", value: MoveEventPayload): void;
}>();

const { elementRef, isDragging, handleDragStart } = useDraggable({
  groups: props.groups,
  data: computed(() => ({
    source: props.source,
    index: props.index,
    item: props.data,
  })),
});

const childrenList = computed<T[]>(() => {
  const raw = props.data.children as unknown as T[] | undefined;
  return raw ?? [];
});
</script>

<template>
  <div class="tree-item" :class="{ dragging: isDragging }">
    <div class="tree-item-row" ref="elementRef" @pointerdown="handleDragStart">
      <TreeDragHandle class="me-2" />
      <span
        v-if="childrenList.length"
        class="expand-toggle"
        @click.stop="expanded = !expanded"
        @pointerdown.stop
      >
        <el-icon size="14">
          <i :class="expanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
        </el-icon>
      </span>
      <span v-else class="expand-placeholder">
        <el-icon size="14" color="grey"> <i class="bi-point"></i> </el-icon
      ></span>
      <slot :item="data" :expanded="expanded" />
    </div>

    <div v-show="expanded" class="tree-children">
      <Tree
        v-if="childrenList.length"
        :data="childrenList"
        :groups="groups"
        :expandable="expandable"
        :parent-id="(data.id as number)"
        @move="emit('move', $event)"
        :disable="isDragging || disable"
      >
        <template #default="{ item, expanded: treeExpanded }">
          <slot
            v-bind="{
              item,
              expanded: treeExpanded,
            }"
          />
        </template>
      </Tree>
    </div>
    <Between
      :at="index + 1"
      :level-list="source"
      :parent-id="parentId"
      :groups="groups"
      @move="emit('move', $event)"
      :disable="disable"
    />
  </div>
</template>

<style scoped>
.tree-item {
  margin: 0px 0;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}
.tree-item-row {
  display: flex;
  align-items: center;
  padding: 2px 0;
  border-radius: 6px;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}
.tree-item.dragging .tree-item-row {
  opacity: 0.6;
}
.tree-children {
  margin-left: 4px;
  padding-left: 4px;
  min-width: 0;
  overflow: hidden;
}
.tree-children-overed {
  background: rgba(147, 197, 253, 0.2);
}
.expand-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
}
.expand-toggle:hover {
  background: rgba(0, 0, 0, 0.05);
}
.expand-placeholder {
  width: 18px;
  flex-shrink: 0;
}
</style>
